import {
  Artist,
  Artwork,
  Exhibition,
  PaginatedResponse,
  SearchResponse,
  ArtistDetailsResponse
} from '../types';

// Use environment variable for API base URL, fallback to direct backend connection
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Get API key from environment variable
const API_KEY = process.env.REACT_APP_API_KEY;

class ApiError extends Error {
  status: number;
  
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `HTTP error! status: ${response.status}`,
      response.status
    );
  }
  
  return response.json();
};

const apiService = {
  // Artists API
  async getArtists(params?: {
    page?: number;
    limit?: number;
    featured?: boolean;
  }): Promise<PaginatedResponse<Artist>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.featured) searchParams.set('featured', 'true');
    
    const url = `${API_BASE}/artists${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<PaginatedResponse<Artist>>(response);
  },

  async getArtistBySlug(slug: string): Promise<ArtistDetailsResponse> {
    const response = await fetch(`${API_BASE}/artists/${slug}`, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<ArtistDetailsResponse>(response);
  },

  // Artworks API
  async getArtworks(params?: {
    page?: number;
    limit?: number;
    artist_id?: string;
    featured?: boolean;
  }): Promise<PaginatedResponse<Artwork>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.artist_id) searchParams.set('artist_id', params.artist_id);
    if (params?.featured) searchParams.set('featured', 'true');
    
    const url = `${API_BASE}/artworks${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<PaginatedResponse<Artwork>>(response);
  },

  async getArtworkById(id: string): Promise<{ artwork: Artwork }> {
    const response = await fetch(`${API_BASE}/artworks/${id}`, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<{ artwork: Artwork }>(response);
  },

  // Exhibitions API
  async getExhibitions(params?: {
    page?: number;
    limit?: number;
    status?: 'upcoming' | 'current' | 'past';
    featured?: boolean;
  }): Promise<PaginatedResponse<Exhibition>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.featured) searchParams.set('featured', 'true');
    
    const url = `${API_BASE}/exhibitions${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<PaginatedResponse<Exhibition>>(response);
  },

  async getExhibitionArtists(exhibitionId: string): Promise<{ data: Artist[] }> {
    const response = await fetch(`${API_BASE}/exhibitions/${exhibitionId}/artists`, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<{ data: Artist[] }>(response);
  },

  // Search API
  async search(query: string): Promise<SearchResponse> {
    const response = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse<SearchResponse>(response);
  },

  // Homepage Settings API
  async getHomepageSettings() {
    const response = await fetch(`${API_BASE}/homepage/settings`, {
      headers: {
        'X-API-Key': API_KEY || '',
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  }
};

export { apiService };
export default apiService;